import {
  Badge,
  Button,
  Card,
  Divider,
  Group,
  Modal,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import { Download, FileSpreadsheet } from 'lucide-react';
import type { WorkspaceExportFileId } from '../export';

type WorkspaceExcelExportPreview = {
  exportId: WorkspaceExportFileId;
  label: string;
  filename: string;
  sheetNames: string[];
  sheetCount: number;
  hasData: boolean;
};

type WorkspaceExportModalProps = {
  opened: boolean;
  onClose: () => void;
  exportingId: WorkspaceExportFileId | null;
  isPreparingExportData: boolean;
  onExport: (exportId: WorkspaceExportFileId) => void;
  canExport: boolean;
  excelExports: WorkspaceExcelExportPreview[];
};

const TREND_EXPORT_IDS = new Set<WorkspaceExportFileId>([
  'reale',
  'passa',
  'norm',
  'norm-passa',
]);

const formatSheetPreview = (sheetNames: string[]) => {
  if (sheetNames.length === 0) {
    return 'Nessun foglio con dati';
  }

  const preview = sheetNames.slice(0, 3).join(' · ');

  if (sheetNames.length <= 3) {
    return preview;
  }

  return `${preview} · +${sheetNames.length - 3} altri`;
};

type ExportOptionCardProps = {
  exportPreview: WorkspaceExcelExportPreview;
  canExport: boolean;
  exportingId: WorkspaceExportFileId | null;
  isPreparingExportData: boolean;
  onExport: (exportId: WorkspaceExportFileId) => void;
};

function ExportOptionCard({
  exportPreview,
  canExport,
  exportingId,
  isPreparingExportData,
  onExport,
}: ExportOptionCardProps) {
  const isExportingThis = exportingId === exportPreview.exportId;
  const isBusy = exportingId !== null && !isExportingThis;
  const showLoading = isPreparingExportData || isExportingThis;

  return (
    <Card withBorder padding='lg' radius='md' h='100%'>
      <Stack gap='md' h='100%' justify='space-between'>
        <Stack gap='sm'>
          <Group
            justify='space-between'
            align='flex-start'
            wrap='nowrap'
            gap='sm'
          >
            <Group
              gap='sm'
              wrap='nowrap'
              align='flex-start'
              style={{ flex: 1, minWidth: 0 }}
            >
              <FileSpreadsheet
                size={18}
                strokeWidth={1.75}
                style={{ flexShrink: 0, marginTop: 2 }}
              />
              <Stack gap={4} style={{ flex: 1, minWidth: 0 }}>
                <Text fw={600} size='sm'>
                  {exportPreview.label}
                </Text>
                <Text size='xs' c='dimmed' lineClamp={2}>
                  {isPreparingExportData
                    ? 'Preparazione dati in corso…'
                    : exportPreview.filename}
                </Text>
              </Stack>
            </Group>
            <Badge
              variant='light'
              color={
                isPreparingExportData
                  ? 'gray'
                  : exportPreview.hasData
                    ? 'blue'
                    : 'gray'
              }
            >
              {isPreparingExportData
                ? '…'
                : `${exportPreview.sheetCount} fogli`}
            </Badge>
          </Group>

          <Text size='xs' c='dimmed' lineClamp={2}>
            {isPreparingExportData
              ? 'Caricamento sessioni in corso'
              : formatSheetPreview(exportPreview.sheetNames)}
          </Text>
        </Stack>

        <Button
          variant='light'
          leftSection={<Download size={16} />}
          onClick={() => onExport(exportPreview.exportId)}
          loading={showLoading}
          disabled={
            !canExport ||
            isBusy ||
            isPreparingExportData ||
            !exportPreview.hasData
          }
          fullWidth
          title={
            !canExport
              ? 'Seleziona un gruppo con almeno una sessione'
              : isPreparingExportData
                ? 'Attendi il completamento del caricamento sessioni'
                : !exportPreview.hasData
                  ? 'Nessun dato disponibile per questo file'
                  : undefined
          }
        >
          {isPreparingExportData
            ? 'Preparazione…'
            : isExportingThis
              ? 'Generazione…'
              : 'Scarica Excel'}
        </Button>
      </Stack>
    </Card>
  );
}

export function WorkspaceExportModal({
  opened,
  onClose,
  exportingId,
  isPreparingExportData,
  onExport,
  canExport,
  excelExports,
}: WorkspaceExportModalProps) {
  const trendExports = excelExports.filter(exportPreview =>
    TREND_EXPORT_IDS.has(exportPreview.exportId)
  );
  const otherExports = excelExports.filter(
    exportPreview => !TREND_EXPORT_IDS.has(exportPreview.exportId)
  );

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title='Esporta analisi'
      centered
      size='xl'
      padding='xl'
    >
      <Stack gap='xl'>
        <Text size='sm' c='dimmed'>
          Ogni file ha un download dedicato. I file evoluzione segnali
          contengono un foglio per ogni combinazione segnale/tratto. Lista
          sessioni e Validazione statistica sono separati. Il file Spettri
          rispetta sessione selezionata, zoom temporale/frequenza e scala log
          dell’interfaccia.
        </Text>

        <Stack gap='md'>
          <Title order={5}>Evoluzione segnali</Title>
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing='md'>
            {trendExports.map(exportPreview => (
              <ExportOptionCard
                key={exportPreview.exportId}
                exportPreview={exportPreview}
                canExport={canExport}
                exportingId={exportingId}
                isPreparingExportData={isPreparingExportData}
                onExport={onExport}
              />
            ))}
          </SimpleGrid>
        </Stack>

        <Divider />

        <Stack gap='md'>
          <Title order={5}>Report e spettri</Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing='md'>
            {otherExports.map(exportPreview => (
              <ExportOptionCard
                key={exportPreview.exportId}
                exportPreview={exportPreview}
                canExport={canExport}
                exportingId={exportingId}
                isPreparingExportData={isPreparingExportData}
                onExport={onExport}
              />
            ))}
          </SimpleGrid>
        </Stack>

        <Group justify='flex-end'>
          <Button
            variant='subtle'
            onClick={onClose}
            disabled={exportingId !== null}
          >
            Chiudi
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
